import { isType, Action } from "typescript-fsa";
import update from "immutability-helper";
import log from "loglevel";
import { Map } from "immutable";

import { isFailedResponseUnauthenticatedNotLogin } from "@api/actions";
import { logout } from "@auth/actions";
import {
    PaginationResult,
    makeMapLoadedModel,
    makeMergeEntities,
    makeHandleShowFailure,
} from "@orm/models";
import * as actions from "../actions";
import {
    Bundle,
    BundleStatus,
    UploadInternalState as State,
    UploadNormalisedEntities,
} from "../models";

const getInitialState = () => ({
    entities: {
        bundles: Map<number, Bundle>(),
        bundleStatuses: Map<string, BundleStatus>(),
    },
    pagination: null,
    list: [],
} as State);

const mapLoadedBundle = makeMapLoadedModel<Bundle, number>("bundle", Bundle);
const mergeEntities = makeMergeEntities<State, UploadNormalisedEntities>(
    ["bundles", "bundleStatuses"],
    { bundles: mapLoadedBundle },
);
const bundlesShowFailure = makeHandleShowFailure<State, Bundle, UploadNormalisedEntities>(
    Bundle,
    "bundles",
);

const reduce = (state: State = getInitialState(), action: Action<any>): State => {
    // State should never be null...
    if (state === null) {
        log.warn("bundles state setting to null, resetting to initialState.");
        state = {} as State;
        state = update(state, { $set: getInitialState() });
    }

    if (isType(action, actions.bundlesIndexSuccess)) {
        // Successful bundlesIndex
        return bundlesIndexSuccess(state, action);
    }
    else if (isType(action, actions.bundlesIndexFailure)) {
        // Failed bundlesIndex
    }
    else if (isType(action, actions.bundlesShowSuccess)) {
        // Successful bundlesShow
        return mergeEntities(state, action.payload.entities, "bundle");
    }
    else if (isType(action, actions.bundlesShowFailure)) {
        // Failed bundlesShow
        return bundlesShowFailure(state, action.payload);
    }
    else if (isType(action, actions.bundlesStoreSuccess)) {
        // Successful bundlesStore
        return bundlesStoreSuccess(state, action);
    }
    else if (isType(action, actions.bundlesUpdateSuccess)) {
        // Successful bundlesUpdate
        return setBundleStatusName(state, action.payload, "bundle-updated-2");
    }
    else if (isType(action, actions.bundlesStoreAlbumSuccess)) {
        // Successful bundlesStoreAlbum
        return setBundleStatusName(state, action.payload[0], "album-created");
    }
    else if (isType(action, actions.bundlesUploadZipSuccess)) {
        // Successful bundlesUploadZip
        return setBundleStatusName(state, action.payload, "zip-uploaded-2");
    }
    else if (isType(action, actions.bundlesCompleteSuccess)) {
        // Successful bundlesComplete
        return setBundleStatusName(state, action.payload, "completing");
    }
    else if (isType(action, actions.bundlesStoreAlbumFailure)
        || isType(action, actions.bundlesUploadZipFailure)
        || isType(action, actions.bundlesCompleteFailure)
    ) {
        // Failed bundlesStoreAlbum, bundlesUploadZip, bundlesComplete
        return setBundleStatusName(state, action.payload.request.getId(), "error");
    }
    else if (isType(action, logout) || isFailedResponseUnauthenticatedNotLogin(action)) {
        // Clear the state on logout.
        return update(state, { $set: getInitialState() });
    }
    // Otherwise we do nothing
    return state;
};

const bundlesIndexSuccess = (state: State, action: Action<[PaginationResult, actions.BundlesResult]>) => {
    const pagination = action.payload[0];
    const { result, entities } = action.payload[1];
    // If the pagination is the first page, replace all the bundles.
    let fullList = state.list.concat(result);
    if (pagination.page === 1) {
        fullList = result;
    }
    const newState = update(state, {
        pagination: { $set: pagination },
        list: { $set: fullList },
    });

    return mergeEntities(newState, entities, "bundle");
};

const bundlesStoreSuccess = (state: State, { payload }: Action<number>) => {
    const newList = [payload].concat(state.list);
    return update(state, {
        list: { $set: newList },
    });
};

const setBundleStatusName = (state: State, bundleId: number, statusName: string) => {
    const bundles = state.entities.bundles;
    if (bundles.has(bundleId)) {
        const bundle = bundles.get(bundleId).setCast("statusName", statusName);
        return update(state, {
            entities: {
                bundles: { $merge: bundles.set(bundleId, bundle) },
            },
        });
    }
    return state;
};

export { reduce as upload, getInitialState };
